import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import logo from '../assets/logo.png';

const API_BASE_URL = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? '' : 'http://localhost:5000');

const LoginPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    username: '',
    password: ''
  });
  const [loginError, setLoginError] = useState('');
  const [focusedField, setFocusedField] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // 이미 로그인된 상태면 관리자 페이지로 리다이렉트
  useEffect(() => {
    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    if (token) {
      navigate('/admin', { replace: true });
    }
  }, [navigate]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    setLoginError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.username && !formData.password) {
      setLoginError('아이디와 비밀번호를 입력해주세요.');
      return;
    }
    if (!formData.username) {
      setLoginError('아이디를 입력해주세요.');
      return;
    }
    if (!formData.password) {
      setLoginError('비밀번호를 입력해주세요.');
      return;
    }

    setIsLoading(true);
    setLoginError('');

    try {
      // API 호출로 로그인 처리
      const response = await fetch(`${API_BASE_URL}/api/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          username: formData.username,
          password: formData.password
        })
      });

      const data = await response.json();

      if (data.success) {
        // 기존 인증 정보 모두 삭제
        localStorage.removeItem('token');
        localStorage.removeItem('currentUser');
        sessionStorage.removeItem('token');
        sessionStorage.removeItem('currentUser');

        // JWT 토큰과 사용자 정보 저장 (항상 localStorage)
        localStorage.setItem('token', data.token);
        localStorage.setItem('currentUser', JSON.stringify(data.user));

        // 매장 변경 감지 및 localStorage 초기화
        try {
          const payload = JSON.parse(atob(data.token.split('.')[1]));
          const newStoreId = payload.id;
          const oldStoreId = localStorage.getItem('currentStoreId');

          if (oldStoreId && parseInt(oldStoreId) !== newStoreId) {
            // 매장이 변경됨 - 이전 매장 데이터 삭제
            localStorage.removeItem('menuData');
            localStorage.removeItem('cartData');
            console.log(`[Store Changed] ${oldStoreId} → ${newStoreId}, localStorage cleared`);
          }

          // 새 storeId 저장 (항상 localStorage에 저장)
          localStorage.setItem('currentStoreId', newStoreId.toString());
        } catch (error) {
          console.error('Failed to decode token for store check:', error);
        }

        navigate('/admin');
      } else {
        setLoginError(data.message || '아이디 또는 비밀번호가 잘못 되었습니다. 다시 입력해주세요.');
        setIsLoading(false);
      }
    } catch (error) {
      console.error('로그인 오류:', error);

      // 백엔드가 없을 때 임시 처리
      const users = JSON.parse(localStorage.getItem('users') || '[]');
      const user = users.find(u =>
        u.username === formData.username &&
        u.password === formData.password
      );

      if (user) {
        // 기존 인증 정보 모두 삭제
        localStorage.removeItem('token');
        localStorage.removeItem('currentUser');
        sessionStorage.removeItem('token');
        sessionStorage.removeItem('currentUser');

        localStorage.setItem('currentUser', JSON.stringify({
          username: user.username,
          businessNumber: user.businessNumber
        }));

        navigate('/admin');
      } else {
        setLoginError('아이디 또는 비밀번호가 잘못 되었습니다. 다시 입력해주세요.');
      }
      setIsLoading(false);
    }
  };

  // 화면 크기에 따른 스케일 조정
  useEffect(() => {
    const scaleUI = () => {
      const wrapper = document.querySelector('.login-wrapper');
      if (wrapper) {
        const viewportWidth = window.innerWidth;
        const scale = Math.min(viewportWidth / 400, 1.5);
        wrapper.style.zoom = scale;
      }
    };
    scaleUI();
  }, []);

  return (
    <>
      <style jsx="true">{`
        .login-page {
          min-height: 100vh;
          background-color: #FFFFFF;
          display: flex;
          justify-content: center;
          align-items: flex-start;
          padding-top: 40px;
          -webkit-text-size-adjust: 100%;
          text-size-adjust: 100%;
        }

        .login-wrapper {
          width: 400px;
          transform-origin: top center;
        }

        @media screen and (min-width: 401px) {
          .login-wrapper {
            zoom: calc(100vw / 400);
          }
        }

        @media screen and (min-width: 600px) {
          .login-wrapper {
            zoom: 1.5;
          }
        }

        @media screen and (max-width: 400px) {
          .login-wrapper {
            width: 400px;
            zoom: calc(100vw / 400);
          }
        }

        .login-input::placeholder {
          color: transparent;
        }
        .login-input {
          caret-color: #FDCD27;
        }
        .login-input:focus {
          outline: none;
        }
        .floating-label {
          position: absolute;
          left: 17px;
          top: 50%;
          transform: translateY(-50%);
          font-size: 15px;
          font-weight: 500;
          color: #9D9D9D;
          pointer-events: none;
          transition: all 0.2s ease;
          transform-origin: left center;
          z-index: 2;
        }
        .floating-label.active {
          top: 12px;
          transform: translateY(0);
          font-size: 12px;
        }
        .login-input:-webkit-autofill,
        .login-input:-webkit-autofill:hover,
        .login-input:-webkit-autofill:focus,
        .login-input:-webkit-autofill:active {
          -webkit-box-shadow: 0 0 0 1000px #FFFFFF inset !important;
          box-shadow: 0 0 0 1000px #FFFFFF inset !important;
          -webkit-text-fill-color: #000000 !important;
          font-size: 15px !important;
          font-weight: 500 !important;
          font-family: 'Pretendard', -apple-system, BlinkMacSystemFont, sans-serif !important;
        }
        .login-page button:focus {
          outline: none;
        }
        .login-page label {
          -webkit-tap-highlight-color: transparent;
          outline: none;
        }
      `}</style>
      <div className="login-page">
        <div className="login-wrapper">
          <div style={styles.card}>
        <div style={styles.logoWrapper}>
          <img src={logo} alt="로고" style={styles.logo} />
        </div>

        <form onSubmit={handleSubmit} style={styles.form}>
          <div style={styles.inputGroupCombined}>
            <div style={{ position: 'relative' }}>
              <input
                type="text"
                name="username"
                className="login-input"
                value={formData.username}
                onChange={handleChange}
                onFocus={() => setFocusedField('username')}
                onBlur={() => setFocusedField(null)}
                style={{
                  ...styles.input,
                  ...styles.inputTop,
                  borderColor: focusedField === 'username' ? '#FDCD27' : '#D9D9D9',
                  borderWidth: focusedField === 'username' ? '1.5px' : '1px',
                  zIndex: focusedField === 'username' ? 1 : 0,
                  paddingTop: (focusedField === 'username' || formData.username) ? '18px' : '12px',
                  paddingBottom: (focusedField === 'username' || formData.username) ? '2px' : '12px'
                }}
                placeholder="아이디"
                autoFocus
              />
              <span className={`floating-label${focusedField === 'username' || formData.username ? ' active' : ''}`}>
                아이디
              </span>
            </div>
            <div style={{
              ...styles.passwordWrapper,
              marginTop: '-1px'
            }}>
              <input
                type={showPassword ? 'text' : 'password'}
                name="password"
                className="login-input"
                value={formData.password}
                onChange={handleChange}
                onFocus={() => setFocusedField('password')}
                onBlur={() => setFocusedField(null)}
                style={{
                  ...styles.input,
                  ...styles.inputBottom,
                  borderColor: focusedField === 'password' ? '#FDCD27' : '#D9D9D9',
                  borderWidth: focusedField === 'password' ? '1.5px' : '1px',
                  zIndex: focusedField === 'password' ? 1 : 0,
                  paddingRight: '48px',
                  paddingTop: (focusedField === 'password' || formData.password) ? '18px' : '12px',
                  paddingBottom: (focusedField === 'password' || formData.password) ? '2px' : '12px'
                }}
                placeholder="비밀번호"
              />
              <span className={`floating-label${focusedField === 'password' || formData.password ? ' active' : ''}`}>
                비밀번호
              </span>
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={styles.passwordToggle}
              >
                {showPassword ? (
                  <svg width="18" height="13" viewBox="0 0 18 13" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M10.8828 6.18359C10.8828 6.80688 10.6352 7.40464 10.1945 7.84536C9.75376 8.28609 9.156 8.53369 8.53271 8.53369C7.90943 8.53369 7.31167 8.28609 6.87094 7.84536C6.43022 7.40464 6.18262 6.80688 6.18262 6.18359C6.18262 5.56031 6.43022 4.96255 6.87094 4.52182C7.31167 4.08109 7.90943 3.8335 8.53271 3.8335C9.156 3.8335 9.75376 4.08109 10.1945 4.52182C10.6352 4.96255 10.8828 5.56031 10.8828 6.18359Z" stroke="#9D9D9D" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M0.700195 6.18357C1.95358 2.97412 4.88024 0.700012 8.53385 0.700012C12.1875 0.700012 15.1141 2.97412 16.3675 6.18357C15.1141 9.39302 12.1875 11.6671 8.53385 11.6671C4.88024 11.6671 1.95358 9.39302 0.700195 6.18357Z" stroke="#9D9D9D" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                ) : (
                  <svg width="18" height="14" viewBox="0 0 18 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M7.54016 1.50721C7.87046 1.46897 8.2027 1.44987 8.53521 1.45001C12.1895 1.45001 15.1166 3.7247 16.3702 6.93496C16.0667 7.71582 15.6567 8.45097 15.1519 9.11953M4.24162 2.64025C2.64328 3.61578 1.40535 5.12728 0.700195 6.93496C1.9538 10.1452 4.88096 12.4199 8.53521 12.4199C10.049 12.4279 11.5354 12.0156 12.8288 11.2289M6.87419 5.2738C6.6559 5.4921 6.48275 5.75126 6.36462 6.03648C6.24648 6.32171 6.18568 6.62741 6.18568 6.93613C6.18568 7.24485 6.24648 7.55055 6.36462 7.83578C6.48275 8.121 6.6559 8.38016 6.87419 8.59846C7.09247 8.81676 7.35161 8.98993 7.63681 9.10807C7.92201 9.22621 8.22769 9.28702 8.53638 9.28702C8.84508 9.28702 9.15076 9.22621 9.43596 9.10807C9.72116 8.98993 9.9803 8.81676 10.1986 8.59846" stroke="#9D9D9D" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M2.26855 0.700012L14.8046 12.7" stroke="#9D9D9D" strokeWidth="1.4" strokeLinecap="round"/>
                  </svg>
                )}
              </button>
            </div>
          </div>

          <div style={styles.buttonWrapper}>
            {loginError && (
              <div style={styles.error}>
                {loginError}
              </div>
            )}
            <button
              type="submit"
              style={{
                ...styles.submitButton,
                opacity: isLoading ? 0.7 : 1,
                cursor: isLoading ? 'not-allowed' : 'pointer'
              }}
              disabled={isLoading}
            >
              {isLoading ? '로그인 중...' : '로그인'}
            </button>
          </div>
        </form>

        <div style={styles.linkGroup}>
          <a href="/signup" style={styles.linkItem}>회원가입</a>
          <span style={styles.linkDivider}>|</span>
          <a href="https://woworder.kr/account-deletion" style={styles.linkItem}>계정 삭제하기</a>
        </div>
        </div>
        </div>
      </div>
    </>
  );
};

const styles = {
  card: {
    backgroundColor: '#FFFFFF',
    padding: '0 20px',
    width: '100%',
    boxSizing: 'border-box'
  },
  logoWrapper: {
    textAlign: 'center',
    marginBottom: '20px'
  },
  logo: {
    width: '91px',
    height: '91px',
    objectFit: 'contain'
  },
  form: {
    display: 'flex',
    flexDirection: 'column'
  },
  inputGroupCombined: {
    display: 'flex',
    flexDirection: 'column'
  },
  inputTop: {
    borderRadius: '8px 8px 0 0',
    position: 'relative'
  },
  inputBottom: {
    borderRadius: '0 0 8px 8px',
    position: 'relative'
  },
  passwordWrapper: {
    position: 'relative',
    width: '100%'
  },
  passwordToggle: {
    position: 'absolute',
    right: '16px',
    top: '50%',
    transform: 'translateY(-50%)',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '4px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 2
  },
  input: {
    width: '100%',
    height: '60px',
    padding: '12px 16px',
    border: '1px solid #D9D9D9',
    borderRadius: '8px',
    backgroundColor: '#FFFFFF',
    fontSize: '15px',
    fontWeight: '500',
    fontFamily: 'inherit',
    outline: 'none',
    boxSizing: 'border-box',
    transition: 'border-color 0.2s, padding 0.2s'
  },
  buttonWrapper: {
    display: 'flex',
    flexDirection: 'column',
    marginTop: '28px'
  },
  error: {
    color: '#F41717',
    fontSize: '14px',
    fontWeight: '600',
    textAlign: 'left',
    marginBottom: '18px'
  },
  submitButton: {
    width: '100%',
    height: '50px',
    backgroundColor: '#FDCD27',
    color: '#FFFFFF',
    border: 'none',
    borderRadius: '8px',
    fontSize: '18px',
    fontWeight: '900',
    lineHeight: '18px',
    cursor: 'pointer'
  },
  linkGroup: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '12px',
    marginTop: '20px'
  },
  linkItem: {
    fontSize: '14px',
    fontWeight: '500',
    color: 'rgb(167, 167, 167)',
    textDecoration: 'none'
  },
  linkDivider: {
    fontSize: '14px',
    color: 'rgb(167, 167, 167)'
  }
};

export default LoginPage;
